import { useEffect, useState } from "react";
import { Printer, Download, Loader2, Contact2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { openPdf, downloadPdf } from "@/lib/documents";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/components/checkout/buttonStyles";

let listener = null;

/** Adds `show_rates=0` to a registration-card URL when rates must be hidden. */
export const regCardUrlWithRates = (url, showRates) => (showRates ? url : `${url}${url.includes("?") ? "&" : "?"}show_rates=0`);

/**
 * REGCARD-RATES · Ask "Show Rates?" before every registration-card print/download.
 * action: "print" (opens inline) | "download". Toggle always resets to ON on open — never persisted.
 */
export function requestRegCard({ url, filename = "RegistrationCard.pdf", action = "print", title }) {
  if (listener) listener({ url, filename, action, title });
  else openPdf(url, filename);
}

export function RegCardOptionsHost() {
  const [req, setReq] = useState(null);
  const [showRates, setShowRates] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listener = (r) => { setShowRates(true); setBusy(false); setReq(r); };
    return () => { listener = null; };
  }, []);

  const go = async () => {
    if (!req) return;
    const url = regCardUrlWithRates(req.url, showRates);
    if (req.action === "download") {
      setBusy(true);
      try { await downloadPdf(url, req.filename); } catch { /* toast shown by downloadPdf */ }
      finally { setBusy(false); }
    } else {
      openPdf(url, req.filename);
    }
    setReq(null);
  };

  const isDownload = req?.action === "download";
  return (
    <Dialog open={!!req} onOpenChange={(v) => !v && !busy && setReq(null)}>
      <DialogContent className="bg-white border border-[#D5E6E8] md:max-w-[440px] p-0 overflow-hidden" data-hocp-dialog data-testid="regcard-options-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#D5E6E8]">
          <DialogTitle className="font-heading text-[16px] flex items-center gap-2"><Contact2 size={18} className="text-[#014751]" />{req?.title || "Registration Card"}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#3F5257]">Choose what to include before {isDownload ? "downloading" : "printing"} this card.</DialogDescription>
        </DialogHeader>
        <div className="px-5 py-4">
          <label className="flex items-center justify-between gap-4 rounded-lg border border-[#D5E6E8] bg-[#F4FCFC] px-4 py-3 cursor-pointer" data-testid="regcard-show-rates-row">
            <div>
              <div className="text-[13.5px] font-semibold text-[#1A2425]">Show Rates</div>
              <div className="text-[12px] text-[#3F5257] mt-0.5">{showRates ? "Agreed rate and pricing will be printed." : "All rate and pricing details will be hidden (e.g. agency bookings)."}</div>
            </div>
            <Switch checked={showRates} onCheckedChange={setShowRates} aria-label="Show Rates" data-testid="regcard-show-rates-toggle" />
          </label>
        </div>
        <div className="px-5 pb-5 flex flex-wrap justify-end gap-2">
          <button type="button" className={`${BTN_SECONDARY} h-10 px-4`} onClick={() => setReq(null)} disabled={busy} data-testid="regcard-options-cancel">Cancel</button>
          <button type="button" className={`${BTN_PRIMARY} h-10 px-4`} onClick={go} disabled={busy} data-testid="regcard-options-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : isDownload ? <Download size={14} /> : <Printer size={14} />}
            {isDownload ? "Download" : "Print"}{showRates ? "" : " without rates"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
